import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useRegion } from "../contexts/RegionContext";
import FozzilPayCheckout from "./FozzilPayCheckout";
import PaymentsComingSoon from "./PaymentsComingSoon";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const PricingTaels = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { region, formatPrice } = useRegion();
  const [showCheckout, setShowCheckout] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState({
    amount: String(region.intelligence.blacksitePayu),
    name: "T.A.E.L.S Blacksite",
    isRecurring: true
  });

  // FAQ Accordion State
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const pricingFaqs = [
    {
      q: "Can I use T.A.E.L.S for free without entering payment details?",
      a: "Yes. T.A.E.L.S Pulse is completely free with zero credit card required. You get instantaneous conversational intelligence, web search analysis, and voice interaction right out of the box."
    },
    {
      q: "What is included with T.A.E.L.S Blacksite?",
      a: "Blacksite provides access to our highest-tier deliberative thinking engine, client-side encrypted sessions, air-gapped memory enclaves, priority GPU inference queue, and zero telemetry guarantees."
    },
    {
      q: "Can I cancel or change my plan anytime?",
      a: "Yes. You can cancel, upgrade, or downgrade your subscription at any time with a single click from your Account settings. Your access continues until the end of the billing period with no penalty."
    },
    {
      q: "What payment methods are supported?",
      a: "We support major credit/debit cards (Visa, MasterCard, American Express), UPI, Net Banking, and FozzilPay with multi-currency pricing."
    },
    {
      q: "Are my queries or data ever used to train AI models?",
      a: "Never. Under both Pulse and Blacksite tiers, FOZZIL enforces a strict Zero Model Training policy. Your proprietary source code, documents, queries, and personal data remain 100% confidential."
    }
  ];

  useEffect(() => {
    document.title = "T.A.E.L.S Plans & Pricing — FOZZIL";
    window.scrollTo(0, 0);
  }, []);

  const handleBlacksiteUpgrade = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    // Payments coming soon — show coming soon modal instead of checkout
    setShowComingSoon(true);
  };

  const handlePaymentSuccess = (data) => {
    console.log("Payment successful:", data);
    window.location.href = `https://taels.fozzil.in/payment-success?txnid=${data.txnid || ''}`;
  };

  return (
    <>
      <div className="bg-black text-white font-secondary min-h-[100dvh] flex flex-col box-border relative overflow-x-hidden">
        {/* Cinematic Background Elements */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/10 blur-[150px] rounded-full -translate-y-1/2" />
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-purple-600/5 blur-[180px] rounded-full translate-y-1/2" />
        </div>

        <button
          onClick={() => navigate("/")}
          className="fixed top-4 md:top-8 right-4 md:right-8 z-50 w-12 h-12 flex items-center justify-center text-white/50 hover:text-white transition-all duration-300 hover:bg-white/10 rounded-full border border-white/5 backdrop-blur-md"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <section className="text-center pt-24 md:pt-32 pb-8 max-w-4xl mx-auto px-6 relative z-10">
          <motion.h4 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-blue-500 font-bold tracking-[0.4em] uppercase text-xs mb-4"
          >
            T.A.E.L.S Foundation
          </motion.h4>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-primary text-3xl md:text-6xl uppercase mb-8 text-white tracking-tighter"
          >
            T.A.E.L.S Plans
          </motion.h1>
          
          {/* Tab Switcher */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center p-1 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full w-fit mx-auto mb-8 md:mb-12"
          >
            <button
              onClick={() => navigate("/pricing-taels")}
              className="px-6 md:px-8 py-2 md:py-2.5 rounded-full transition-all duration-500 bg-white text-black text-xs md:text-sm font-bold shadow-[0_0_20px_rgba(255,255,255,0.2)]"
            >
              T.A.E.L.S
            </button>
            <button
              onClick={() => navigate("/pricing-medos")}
              className="px-6 md:px-8 py-2 md:py-2.5 rounded-full transition-all duration-500 bg-transparent text-white/60 hover:text-white text-xs md:text-sm font-medium"
            >
              MEDOS
            </button>
          </motion.div>
        </section>

        <section className="flex justify-center gap-6 md:gap-8 flex-wrap max-w-7xl mx-auto px-4 py-8 md:py-12 flex-1 items-stretch relative z-10">
          {/* BASIC PLAN */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="group bg-black/40 backdrop-blur-3xl border border-white/5 rounded-[32px] p-6 md:p-10 flex flex-col items-start text-left box-border w-full sm:w-[320px] transition-all duration-700 relative hover:border-blue-500/20 hover:shadow-2xl hover:shadow-blue-500/5 hover:-translate-y-2 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <h2 className="font-primary text-xl uppercase text-white/50 group-hover:text-blue-400 transition-colors mb-2 tracking-[0.1em]">BASIC</h2>
            <div className="flex items-end mb-6">
              <span className="font-primary text-4xl text-white">Free</span>
            </div>
            
            <button className="w-full py-3 px-6 mb-8 text-xs font-bold text-white/40 bg-white/5 border border-white/5 rounded-full cursor-not-allowed uppercase tracking-widest transition-all">
                Current Plan
            </button>
            
            <p className="text-sm text-zinc-500 leading-relaxed mb-8 font-light">
              Your entry into the world of T.A.E.L.S. Core features for everyday use.
            </p>
            
            <div className="space-y-4 w-full">
              <h3 className="text-xs font-bold text-blue-500/50 uppercase tracking-[0.2em]">Capabilities</h3>
              <ul className="space-y-4">
                {['Ask, Search, & Reasoning', 'Lightweight tasks', 'Voice command interface', 'Limited file uploads'].map((feat, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-zinc-400 font-light group-hover:text-zinc-200 transition-colors">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500/30 group-hover:bg-blue-500 transition-all border border-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.3)]" />
                    {feat}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* BLACKSITE PLAN */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="group bg-black border border-blue-500/30 rounded-[32px] p-8 md:p-12 flex flex-col items-start text-left box-border w-full sm:w-[380px] transition-all duration-700 relative z-20 shadow-[0_0_80px_rgba(59,130,246,0.1)] hover:shadow-[0_0_100px_rgba(59,130,246,0.2)] hover:border-blue-400 hover:-translate-y-3 overflow-hidden backdrop-blur-3xl"
          >
            {/* Animated Glossy Shine */}
            <motion.div 
               animate={{ x: ['-200%', '200%'] }}
               transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
               className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/[0.05] to-transparent skew-x-[35deg] pointer-events-none"
            />
            
            <div className="absolute top-6 right-6 bg-blue-500/10 text-blue-400 text-[10px] uppercase font-black px-3 py-1 rounded-full border border-blue-500/20">
              Most Advanced
            </div>

            <h2 className="font-primary text-2xl uppercase text-white mb-2 tracking-tight">BLACKSITE</h2>
            <div className="flex items-end mb-1">
              <span className="font-primary text-4xl md:text-5xl text-white">{formatPrice(region.intelligence.blacksite)}</span>
              <span className="text-zinc-500 ml-2 mb-2 font-medium">/mo</span>
            </div>
            <p className="text-[10px] text-zinc-500 mb-8 font-mono">Exclusive of convenience fees</p>
            
            <button
              onClick={handleBlacksiteUpgrade}
              className="w-full py-3.5 px-6 mb-10 text-sm font-black text-white rounded-full bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 shadow-[0_10px_30px_rgba(37,99,235,0.3)] hover:shadow-[0_15px_40px_rgba(37,99,235,0.5)] transition-all duration-500 uppercase tracking-widest active:scale-95"
            >
              Initialize Upgrade
            </button>
            
            <div className="space-y-6 w-full">
              <h3 className="text-xs font-bold text-blue-500 uppercase tracking-[0.2em]">Next-Gen Intelligence</h3>
              <ul className="space-y-4 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                {[
                  'T.A.E.L.S APEX Thinking Engines',
                  'Deep Research Models',
                  'Stealth / Ghost Mode',
                  'Advanced Creative Suite',
                  'Ultra-Fast Multi-layered Reasoning',
                  'Full Privacy Masking'
                ].map((feat, i) => (
                  <li key={i} className="flex items-center gap-4 text-sm text-zinc-300 group/item">
                    <div className="w-5 h-5 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20 group-hover/item:bg-blue-500/20 transition-colors">
                      <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/></svg>
                    </div>
                    {feat}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* EXTREMIS PLAN */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="group bg-black/40 backdrop-blur-3xl border border-white/5 border-dashed rounded-[32px] p-6 md:p-10 flex flex-col items-center justify-center text-center box-border w-full sm:w-[320px] transition-all duration-700 relative opacity-40 hover:opacity-100"
          >
            <h2 className="font-primary text-xl uppercase text-white/30 group-hover:text-blue-400/50 mb-8 tracking-[0.2em] transition-colors">EXTREMIS</h2>
            <div className="relative w-16 h-16 flex items-center justify-center mb-8">
              <div className="absolute inset-0 bg-blue-500/20 blur-2xl rounded-full scale-150 animate-pulse" />
              <div className="relative w-3 h-3 rounded-full bg-blue-500 shadow-[0_0_20px_rgba(59,130,246,1)]" />
            </div>
            <p className="text-sm font-bold text-zinc-500 uppercase tracking-[0.4em] mb-3">Coming Soon</p>
            <p className="text-xs text-zinc-600 font-light leading-relaxed">The future of hyper-personalized private AI.</p>
          </motion.div>
        </section>

        {/* ANSWERS & SPECIFICATIONS: FREQUENTLY ASKED QUESTIONS */}
        <section className="relative z-10 py-20 px-6 max-w-4xl mx-auto w-full border-t border-white/[0.08]">
          <div className="space-y-4 mb-14 text-center">
            <span className="text-xs font-mono text-blue-400 uppercase tracking-[0.25em]">
              Answers & Specifications
            </span>
            <h2 className="text-3xl sm:text-5xl font-semibold text-white tracking-tight">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {pricingFaqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className="rounded-[24px] bg-white/[0.03] border border-white/[0.06] overflow-hidden transition-all duration-300 hover:border-white/[0.12]"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full flex items-center justify-between p-6 sm:p-7 text-left text-white font-medium text-base sm:text-lg cursor-pointer hover:bg-white/[0.02] transition-colors"
                  >
                    <span className="pr-4">{faq.q}</span>
                    <div className="w-8 h-8 rounded-full bg-white/[0.05] flex items-center justify-center text-zinc-400 shrink-0">
                      {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </div>
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 sm:px-7 pb-6 text-sm sm:text-base text-zinc-400 font-light leading-relaxed border-t border-white/[0.04] pt-4">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </section>

        <footer className="relative z-10 text-center text-[10px] md:text-xs text-zinc-600 px-4 py-12 max-w-2xl mx-auto uppercase tracking-[0.1em]">
          By proceeding, you agree to FOZZIL's 
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="mx-2 text-zinc-400 hover:text-blue-500 transition-colors">Terms</a> 
          & 
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="ml-2 text-zinc-400 hover:text-blue-500 transition-colors">Privacy</a>.
        </footer>
      </div>

      <FozzilPayCheckout
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        amount={checkoutProduct.amount}
        productinfo={checkoutProduct.name}
        isRecurring={checkoutProduct.isRecurring}
        user_id={user?.id || 'guest'}
        firstname={user?.user_metadata?.full_name || user?.email?.split('@')[0] || ''}
        email={user?.email || ''}
        onSuccess={handlePaymentSuccess}
        allowedMethods={['UPI', 'Card', 'Netbanking', 'Wallet']}
      />

      <PaymentsComingSoon isOpen={showComingSoon} onClose={() => setShowComingSoon(false)} />

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(59, 130, 246, 0.2); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(59, 130, 246, 0.4); }
      `}</style>
    </>
  );
};

export default PricingTaels;
